import styles from './master-detail-table-custom.module.scss';
import { Pagination } from '../pagination/pagination';
import { AgGridReact } from 'ag-grid-react';
import { GridApi, ColumnApi, GridReadyEvent, PaginationChangedEvent } from 'ag-grid-community';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InputAdornment } from '@mui/material';
import { ColumnConfig, Icon, MenuItem, Select, TableConfig, TextField, Typography } from '@frontend-workspace/react-shared-lib';
import { AG_GRID_LOCALE_NBNO } from '../simple-table/ag-grid.locale.nb';
import TableFilters, { iTableSwitch } from '../table-filters/table-filters';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface MasterDetailTableCustomProps {
  data: Array<any>,
  masterHeaders: Array<any>,
  detailHeaders: Array<any>,
  detailsCellRenderer: any,
  pageSize?: number,
  onSelectionChanged?: (...args: any[]) => void,
  onGridReady?: (api: GridApi) => void,
  hasColumnFilters?: boolean,
  tableFilters?: Array<iTableSwitch>,
  singleCellSelection?: boolean,
  detailsCellRendererParams?: any,
  tableConfig?: TableConfig
}

export function MasterDetailTableCustom(props: MasterDetailTableCustomProps) {
  if (props.masterHeaders.length === 0) return null;
  const gridRef = useRef() as any;
  const { t, i18n } = useTranslation();
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi | null>(null);
  const [currentPage, setCurrentPage] = useState<number>();
  const [pageCount, setPageCount] = useState<number>();
  const [pageSize, setPageSize] = useState<number>(props.pageSize || 10);
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);

  useEffect(() => {
    if (gridApi) {
      gridApi.setFilterModel(props.tableConfig?.filterModel);
    }
  }, [props.tableConfig]);

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      resizable: true,
      filter: props.hasColumnFilters,
      sortable: true
    };
  }, []);

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
    props.onGridReady?.(params.api);
    // autoSizeColumns();
  }

  const handleFirstDataRender = () => {
    gridPaginationChanged();
    // autoSizeColumns();
  }

  const onSelectionChanged = () => {
    const selectedRows = gridApi?.getSelectedRows();
    props.onSelectionChanged?.(selectedRows);
  };

  const changePageSize = (e: any) => {
    setPageSize(e.target.value);
  }

  const muiPaginationChanged = (e: any, page: number) => {
    gridApi?.paginationGoToPage(page - 1);
  }

  const gridPaginationChanged = (e?: PaginationChangedEvent) => {
    setCurrentPage((gridApi?.paginationGetCurrentPage() || 0) + 1);
    setPageCount(gridApi?.paginationGetTotalPages() || 1);
  }

  const search = (e: any) => {
    gridApi?.setQuickFilter(e.target.value);
  }

  let headers = props.masterHeaders;
  if (props.tableConfig) {
    headers = headers.map(h => {
      const columnDefinition = props.tableConfig?.columns && props.tableConfig?.columns?.find(c => c.columnName === h.field);
      if (columnDefinition) {
        h.hide = columnDefinition.isHidden;
        h.sort = columnDefinition.sortDirection;
      }
      return h;
    });
  };

  const handleSaveTable = (params: any) => {
    if (!props.tableConfig) return;
    const api = params.columnApi;
    const gridApi = params.api;
    const columnState = api.getColumnState();
    const filterModel = gridApi.getFilterModel();
    const values: ColumnConfig[] = columnState.map((column: any) => {
      const colConfig: ColumnConfig = {
        columnName: column.colId,
        isHidden: column.hide,
        sortDirection: column.sort
      }
      return colConfig
    });
    props.tableConfig.onSaveTableConfig(values, filterModel);
  }

  const getContextMenuItems = useCallback((params: any) => {
    let getContextMenuItems: any = ["copy", "export"];

    if (props.tableConfig) {
      getContextMenuItems.push({
        name: t("simpleTable.saveTableConfiguration"),
        action: () => handleSaveTable(params)
      });
      getContextMenuItems.push({
        name: t("simpleTable.resetTableConfiguration"),
        action: () => props.tableConfig?.onSaveTableConfig(null, null)
      })
    }
    return getContextMenuItems;
  }, []);

  return (
    <div style={containerStyle}>
      <div style={gridStyle} className="ag-theme-alpine" data-testid="ag-theme-alpine-master-detail-table-custom">
        <div className="d-flex flex-row flex-wrap mb-3">
          <div className='flex-grow-1'>
            {props.tableFilters &&
              <TableFilters switches={props.tableFilters} />}
          </div>
          <div>
            <TextField
              type="search"
              onChange={search}
              placeholder="Søk i tabell"
              InputProps={{
                startAdornment:
                  <InputAdornment position="start">
                    <Icon>search</Icon>
                  </InputAdornment>
              }}
            />
          </div>
        </div>

        <AgGridReact
          localeText={AG_GRID_LOCALE_NBNO}
          ref={gridRef}
          rowData={props.data}
          onGridReady={onGridReady}
          rowSelection={props.singleCellSelection ? 'false' : 'single'}
          domLayout={'autoHeight'}
          pagination={!!props.pageSize}
          paginationPageSize={pageSize}
          suppressPaginationPanel={true}
          onPaginationChanged={gridPaginationChanged}
          onSelectionChanged={onSelectionChanged}
          onFirstDataRendered={handleFirstDataRender}
          suppressColumnVirtualisation={true}
          defaultColDef={defaultColDef}
          columnDefs={headers}
          masterDetail={true}
          getContextMenuItems={getContextMenuItems}
          detailRowAutoHeight={true}
          detailCellRendererParams={props.detailsCellRendererParams}
          detailCellRenderer={props.detailsCellRenderer}>
        </AgGridReact>

        <div className='d-flex flex-md-row flex-wrap justify-content-end align-items-center'>
          <div className='mt-4 pe-4 d-flex align-items-center'>
            <Typography component='span' variant='body6' className='pe-1'>
              Vis antall:
            </Typography>
            <Select
              value={pageSize}
              onChange={changePageSize}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </div>
          <div className='mt-4 pe-4'>
            <Typography component='span' variant='body6'>
              Total: {gridApi?.getDisplayedRowCount()}
            </Typography>
          </div>
          <div>
            {!!props.pageSize && props.data &&
              <Pagination
                className="d-flex justify-content-end mt-4"
                count={pageCount || 0}
                page={currentPage || 0}
                onChange={(e, page) => muiPaginationChanged(e, page)}
              />
            }
          </div>
        </div>
      </div>
    </div>
  );
}

export default MasterDetailTableCustom;